/* eslint-disable max-lines */
import { isEqual } from 'lodash'
import assignDeep from '@aytm/lib/common/assign-deep'
import SchemaRules from '@aytm/app/stats/lib/query/schema-rules'
import { viewerIsAdmin } from '@aytm/app/stats/lib/page/viewer'
import { collator, setIn, getIn, uniq, is, assocPath, omit, casecmp } from '@aytm/app/stats/lib/util/helpers'

const { ConvertToBasicError } = SchemaRules

const questionAnswerRegExp = new RegExp(
  // the answer group keeps an ecommerce child segment (a1cs2) whole, so the group numbering below is unchanged
  '^([pvx]?q)(\\d+)(?:e(\\d+))?(?:sq(\\d+))?(?:a(\\d+(?:cs\\d+)?))?(?:(r)(\\d*))?([cnp])?(v)?(i)?$',
  'i'
)

const simpleQuestionTypes = ['it', 'im', 'ir', 'va', 'vo', 'ed', 'vr', 'oe', 'cjc', 'cjd', 'emd', 'ad']

export default class BasicFilters {
  constructor(options = {}) {
    this.options = options
    this.operators = {}
    this.clusters = options.clusters || []
    this.surveySchema = options.surveySchema || {}
    this.deepAllowed = options.deepAllowed || false
    this.longAllowed = options.longAllowed || false
    this.tagsAllowed = options.tagsAllowed || false
    this.sentimentsAllowed = options.sentimentsAllowed || false

    // The role is a boot fact, so a caller that does not already hold it need not look it up.
    const isAdmin = this.options.isAdmin ?? viewerIsAdmin()

    const traits = this.surveySchema.traits
    const arrTraitsList = traits.filter(t => 'a' === t.type).map(t => t.id.toUpperCase())
    this.arrTraits = arrTraitsList.filter(trait => isAdmin || 'panel' != trait)

    const { variables = {}, pipingVariables = {}, customVariables = {} } = this.surveySchema || {}
    this.arrVariables = [
      ...Object.entries(variables),
      ...Object.entries(pipingVariables),
      ...Object.entries(customVariables)
    ].reduce(
      (arrVariables, [name, variable]) => arrVariables.concat('a' === variable.type ? [name.toUpperCase()] : []),
      []
    )
  }

  convertToBasic(tree = {}) {
    if (null === tree) return false

    const groups = convertToGroups(tree)
    const filters = groups.map(g => this._treeToFilter(g, {}))
    const filterGroups = filters.reduce((acc, filter) => {
      let arr = Object.keys(filter).map(key => this._transformFilterGroup(key, filter[key]))
      const root = arr.filter(v => Array.isArray(v))
      arr = [arr.filter(v => !Array.isArray(v)).reduce((_acc, _cur) => Object.assign({}, _acc, _cur), {})]
      while (root.length) {
        arr = root.shift().flatMap(r => arr.map(v => Object.assign({}, v, r)))
      }
      return acc.concat(arr)
    }, [])

    let isBasic = true
    const basicKeys = Object.keys(filterGroups[0])
    for (let index = 0; isBasic && index < filterGroups.length; index++) {
      const filter = filterGroups[index]
      if (filter.errors && filter.errors.length) {
        isBasic = false
      } else if (!Object.keys(filter).every(k => basicKeys.includes(k))) {
        isBasic = false
      }
    }

    if (!isBasic) return false

    const flattenFilter = filterGroups.reduce(
      (filters, filter) => this._mergeBasicFilter(filters, filter, true, true),
      {}
    )

    if (flattenFilter.errors) return false
    this._cleanUpMergedFilter(flattenFilter)

    Object.keys(flattenFilter).forEach(key => {
      if (!isBasic) return
      isBasic = false

      const type = this._getQuestionTypeByRef(key) || null

      if (null === type) {
        isBasic = filterGroups.reduce((basic, filter) => basic && this._isBasicTraitFilter(key, filter[key]), true)
        isBasic ||= filterGroups.reduce((basic, filter) => basic && this._isBasicVariableFilter(key, filter[key]), true)
        isBasic ||= filterGroups.reduce((basic, filter) => basic && this._isBasicTagFilter(key, filter[key]), true)
      } else {
        const [checkedKeys, offKeys] = filterGroups.reduce(
          ([checkedKeys, offKeys], filter) => [
            checkedKeys.concat(
              ...Object.keys(flattenFilter[key]).filter(k =>
                Object.hasOwn(filter, key) && Object.keys(filter[key]).includes(k) && 'x' != k
                  ? true == getIn([key, k, 'x'], filter)
                  : true == getIn([key, k], filter)
              )
            ),
            offKeys.concat(
              ...Object.keys(flattenFilter[key]).filter(k =>
                Object.hasOwn(filter, key) && Object.keys(filter[key]).includes(k) && 'x' != k
                  ? false == getIn([key, k, 'x'], filter)
                  : false == getIn([key, k], filter)
              )
            )
          ],
          [[], []]
        )

        const sortedCheckedKeys = uniq(checkedKeys)
        sortedCheckedKeys.sort()

        isBasic =
          ((simpleQuestionTypes.includes(type) || (true !== this.options.sflag && 'md' == type)) &&
            (1 == checkedKeys.length || checkedKeys.length == filterGroups.length)) ||
          (([
            'rb',
            'scx',
            'oex',
            'ro',
            'aro',
            'mx',
            'sr',
            'se',
            'qa',
            'bpm',
            'cb',
            'cn',
            'sh',
            'ec',
            'sft',
            'mp'
          ].includes(type) ||
            (true === this.options.sflag && 'md' == type)) &&
            (0 == checkedKeys.length || checkedKeys.length == filterGroups.length) &&
            (0 == offKeys.length || filterGroups.length == offKeys.length / uniq(offKeys).length)) ||
          (['cx'].includes(type) &&
            (1 == filterGroups.length || filterGroups.length == checkedKeys.length / sortedCheckedKeys.length) &&
            ((this.deepAllowed &&
              isEqual(sortedCheckedKeys, ['R1', 'R2', 'R3', 'R4'].slice(0, sortedCheckedKeys.length))) ||
              (!this.deepAllowed && 1 == checkedKeys.length && 'R1' == checkedKeys[0]))) ||
          ['ps', 'psc'].includes(type) ||
          ['vk'].includes(type) ||
          ['hm', 'mn'].includes(type)
      }
    })

    if (!isBasic) return false

    if (!Array.isArray(flattenFilter)) {
      const surveySchema = this.surveySchema
      const { variables = {}, pipingVariables = {}, customVariables = {} } = surveySchema

      for (const key in flattenFilter) {
        const variable =
          variables[key.toLowerCase()] || pipingVariables[key.toLowerCase()] || customVariables[key.toLowerCase()]

        if (variable != undefined && variable.x) {
          if (Array.isArray(flattenFilter[key].v) && 1 < flattenFilter[key].v.length) {
            isBasic = false
            break
          }

          const [_, k, p] = key.match(/^(\w+)_(\d+)$/)

          flattenFilter[k] ||= {}
          flattenFilter[k][p] = {
            x: 1 == (Array.isArray(flattenFilter[key].v) ? flattenFilter[key].v[0] : flattenFilter[key].v)
          }

          delete flattenFilter[key]
        }
      }
    }

    if (!isBasic) return false

    return flattenFilter
  }

  _getQuestionByRef(ref) {
    const surveySchema = this.surveySchema
    const match = ref.match(/^([pvx])?q(\d+)/i)
    if (match && match[2]) {
      const isVirtual = null != match[1] && 'V' == match[1].toUpperCase()
      const isXpert = null != match[1] && 'X' == match[1].toUpperCase()
      const isPreQ = null != match[1] && 'P' == match[1].toUpperCase()
      const visualPosition = parseInt(match[2], 10)
      const question = isVirtual
        ? getIn([match[0].toLowerCase()], surveySchema.virtual || {})
        : isXpert
          ? surveySchema.xquestions.find(q => q.id == `xq${match[2]}`)
          : surveySchema.questions
              .filter(q => (isPreQ ? q.pre_type : !q.pre_type))
              .find(q => visualPosition == q.visual_position)
      return question
    }
    return null
  }

  _getQuestionTypeByRef(ref) {
    const { type, variant } = this._getQuestionByRef(ref) || {}
    return variant || type
  }

  _valueToCase(v, foo = String.prototype.toLowerCase) {
    return null == v
      ? v
      : is(Array)(v)
        ? v.map(vv => (is(String)(vv) ? foo.apply(vv) : vv))
        : is(String)(v)
          ? foo.apply(v)
          : v
  }

  _transformFilterGroup(key, value) {
    const surveySchema = this.surveySchema

    if ('AGE' == key.toUpperCase() && Array.isArray(value.raw) && surveySchema.traits.some(t => 'age' == t.id)) {
      const [minAge] = this._minAgeRangeLowerBound()
      const [maxAge] = this._maxAgeRangeUpperBound()

      const raw = Object.assign([minAge, maxAge], value.raw)

      const trait = surveySchema.traits.find(t => 'age' == t.id)
      const ranges = trait.values
        .flatMap(v => (v.id.includes('+') ? [v.id.replace('+', ''), 1000000] : v.id.split('-')))
        .map(v => Number(v))
      const i1 = ranges.indexOf(raw[0])
      const i2 = ranges.indexOf(raw[1])
      const values =
        0 == i1 % 2 && 1 == i2 % 2
          ? Array.from({ length: (i2 - i1 + 1) / 2 }).map((_, i) =>
              1000000 === ranges[i1 + 2 * i + 1]
                ? `${ranges[i1 + 2 * i]}+`
                : `${ranges[i1 + 2 * i]}-${ranges[i1 + 2 * i + 1]}`
            )
          : raw

      return { [key]: { raw: raw, v: values, x: value.x } }
    }

    return { [key]: value }
  }

  _isBasicTraitFilter(key, value) {
    const traitKey = key.toLowerCase()
    const surveySchema = this.surveySchema

    const { v, x } = value || {}

    const trait = surveySchema.traits.find(t => traitKey == t.id)
    if (!trait || !x) return false

    const { refsList } = this.surveySchema

    if (!Object.prototype.hasOwnProperty.call(refsList, traitKey.toUpperCase())) {
      return false
    }

    let traitValue = v
    if ((!v || null === v) && 'age' === traitKey) {
      const [maxAge, maxAgeRange] = this._maxAgeRangeLowerBound()
      value.raw && value.raw[0] == maxAge && (traitValue = [maxAgeRange])
    } else if (null !== v && ['state', 'region', 'county', 'country'].includes(traitKey)) {
      traitValue = this._valueToCase(v, String.prototype.toUpperCase)
    } else {
      traitValue = this._valueToCase(v, String.prototype.toLowerCase)
    }

    if (null === traitValue) {
      return false
    }

    return (is(Array)(traitValue) ? traitValue : [traitValue]).reduce(
      (all, val) => all && trait.values.some(v => String(val).toLowerCase() == String(v.id).toLowerCase()),
      true
    )
  }

  _isBasicVariableFilter(key, value) {
    const { v, x } = value || {}
    const variable = key.toLowerCase()
    const surveySchema = this.surveySchema
    const { refsList = {}, variables = {}, pipingVariables = {}, customVariables = {} } = surveySchema

    if (
      (!Object.hasOwn(variables, variable) &&
        !Object.hasOwn(pipingVariables, variable) &&
        !Object.hasOwn(customVariables, variable)) ||
      true !== x
    ) {
      return false
    }

    if (!Object.hasOwn(refsList, variable.toUpperCase())) {
      return false
    }

    const schemaVars = variables[variable] ? variables : pipingVariables[variable] ? pipingVariables : customVariables

    if (!Object.hasOwn(schemaVars, variable)) {
      return false
    }

    if (schemaVars[variable].long && this.longAllowed) return true

    const values = is(Array)(v) ? v : [v]
    if (null === schemaVars[variable].values || (schemaVars[variable].x && 1 != values.length)) return false

    return values.reduce(
      (all, val) =>
        all &&
        1 ==
          schemaVars[variable].values.filter(vrbl =>
            Array.isArray(vrbl) ? casecmp(String(val), String(vrbl)) : casecmp(val, vrbl)
          ).length,
      true
    )
  }

  _isBasicTagFilter(key, value) {
    const [k, token, ref] = (key.match(/^(TAG)(?:\.(SENTIMENT))?\.(\w+)$/, 'gi') || []).splice(1, 4)
    const { v, x } = value || {}

    if (!k || true !== x) return false

    if (
      (token == undefined && (ref == undefined || !this.tagsAllowed)) ||
      (token != undefined && ('sentiment' !== token.toLowerCase() || ref == undefined || !this.sentimentsAllowed))
    ) {
      return false
    }

    if (token != undefined && 'sentiment' === token.toLowerCase()) {
      const { sentiments } = this.surveySchema

      return (
        sentiments[ref] != undefined &&
        (is(Array)(v) ? v : [v]).reduce(
          (all, val) => all && 1 == sentiments[ref].filter(vrbl => casecmp(val, vrbl)).length,
          true
        )
      )
    }

    const { tags } = this.surveySchema

    return (
      tags[ref] != undefined &&
      (is(Array)(v) ? v : [v]).reduce(
        (all, val) => all && 1 == tags[ref].filter(vrbl => casecmp(val, vrbl)).length,
        true
      )
    )
  }

  _isBasicQuestionPSFilter(question, filter) {
    let isBasic = false

    const answerVariants = (
      getIn(
        [0, 'answerVariants'],
        question.batches.filter(b => getIn(['path', 'sq'], filter) == b.position)
      ) || []
    ).filter(av => null != av)

    const raw =
      filter.x && null != filter.raw
        ? Array.from({ length: 2 }, (_, i) =>
            null != filter.raw[i]
              ? answerVariants.some(av => filter.raw[i] == parseFloat(av.text))
                ? filter.raw[i]
                : null
              : null
          ).filter(v => null != v)
        : []

    if (2 == raw.length) {
      filter.v = [raw.join('-')]
      isBasic = true
    }

    return isBasic
  }
  _isBasicQuestionVKFilter(question, filter) {
    let isBasic = false

    const sq = getIn(['path', 'sq'], filter)
    const bPos = getIn(['path', 'a'], filter) + (1 < sq ? 4 : 0) + (2 < sq ? 5 : 0)
    const filterValues = ((question.batches.find(b => bPos == b.position) || {}).filter_values || []).filter(
      v => null !== v
    )
    if (isNaN(bPos)) {
      throw new TypeError('DEBUG')
    }
    // if (9 < bPos) filterValues.reverse()

    const raw =
      filter.x && (null == filter.v || (is(Array)(filter.v) && 1 == filter.v.length)) && null != filter.raw
        ? Array.from({ length: filter.raw ? 2 : 0 }, (_, i) =>
            null != filter.raw[i]
              ? null !=
                filterValues.find((v, j) =>
                  5 > bPos
                    ? filter.raw[i] == Math.round(100 * v)
                    : 10 > bPos
                      ? filter.raw[i] == v
                      : filter.raw[i] == filterValues.length - j
                )
                ? filter.raw[i]
                : null
              : 0 == i
                ? 5 > bPos
                  ? Math.round(100 * filterValues[0])
                  : 10 > bPos
                    ? filterValues[0]
                    : 1
                : 5 > bPos
                  ? Math.round(100 * filterValues.slice(-1)[0])
                  : 10 > bPos
                    ? filterValues.slice(-1)[0]
                    : filterValues.length
          ).filter(v => null !== v)
        : []

    if (2 == raw.length) {
      // filter.v = [raw.map(v => v / (5 > bPos ? 100 : 1)).join('-')]
      filter.v = [raw.join('-')]
      isBasic = true
    } else if (null != filter.v) {
      const values = is(Array)(filter.v) ? filter.v : [filter.v]
      isBasic =
        values.length ==
        values.filter(
          value =>
            null !=
            filterValues.find((v, j) =>
              5 > bPos ? value == Math.round(100 * v) : 10 > bPos ? value == v : value == filterValues.length - j
            )
        ).length
    } else {
      isBasic = false
    }

    return isBasic
  }
  _isBasicQuestionHMFilter(question, keys, filter) {
    let isBasic =
      !Object.prototype.hasOwnProperty.call(filter, 't') &&
      !keys.some(k => getIn([k, 'path', 'a'], filter) || getIn([k, 't'], filter)) &&
      keys.reduce((res, k) => res && (getIn([k, 'path', 'sq'], filter) || 0) <= question.batches.length, true)

    isBasic &&=
      0 < keys.length &&
      1 >= keys.filter(k => true == filter[k].x).length &&
      0 <= keys.filter(k => false == filter[k].x).length

    return isBasic
  }
  _isBasicQuestionFilter(key, filter) {
    const question = this._getQuestionByRef(key)
    const type = this._getQuestionTypeByRef(key)

    const keys = Object.keys(filter)
    const answerVariants = keys.filter(k => /^a\d+(?:cs\d+)?$/i.test(k) && null != filter[k].x)

    const responses = keys
      .filter(k => /^r\d+$/i.test(k) && null != filter[k].x)
      .sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10))

    let isBasic =
      ((simpleQuestionTypes.includes(type) || (true !== this.options.sflag && 'md' == type)) &&
        1 == keys.length &&
        'x' === keys[0] &&
        filter.x) ||
      ((['rb', 'scx', 'oex', 'ro', 'aro', 'mx', 'sr', 'se', 'qa', 'bpm', 'cb', 'cn', 'sh', 'ec', 'sft', 'mp'].includes(
        type
      ) ||
        (true === this.options.sflag && 'md' == type)) &&
        0 < answerVariants.length &&
        answerVariants.length == keys.length &&
        1 >= answerVariants.filter(k => true == filter[k].x).length &&
        0 <= answerVariants.filter(k => false == filter[k].x).length) ||
      (['cx'].includes(type) &&
        keys.length == responses.length &&
        responses.reduce((full, v, i) => full && parseInt(v.slice(1), 10) == i + 1, true) &&
        responses.reduce(
          ([full, answerVariants], k) => {
            const answer =
              full && null != filter[k].v ? answerVariants.find(av => av && casecmp(av.text, filter[k].v)) : null
            null != answer && (filter[k].v = answer.text)
            return [null != answer, null != answer ? answer.answerVariants : null]
          },
          [true, question.answerVariants]
        )[0])

    if (true === this.options.sflag && ['psc'].includes(type)) {
      isBasic = true
    }

    if (['ps'].includes(type) || (true !== this.options.sflag && ['psc'].includes(type))) {
      isBasic = this._isBasicQuestionPSFilter(question, filter)
    }
    if (['vk'].includes(type)) {
      isBasic = this._isBasicQuestionVKFilter(question, filter)
    }
    if (['hm'].includes(type)) {
      isBasic = this._isBasicQuestionHMFilter(question, keys, filter)
    }
    if (['mn'].includes(type)) {
      isBasic = 0 < keys.length && keys.every(k => null != filter[k]?.x)
    }

    return isBasic
  }

  _parseIdentifier(name, x = true, value = null) {
    const NAME = name.toUpperCase()
    const obj = { x }
    null != value && (obj.v = value)

    const { variables = {}, pipingVariables = {}, customVariables = {} } = this.surveySchema
    const variable = name.toLowerCase()
    const isVariable =
      Object.hasOwn(variables, variable) ||
      Object.hasOwn(pipingVariables, variable) ||
      Object.hasOwn(customVariables, variable)

    const match = isVariable ? null : NAME.match(questionAnswerRegExp)
    if (match) {
      const path = [`${match[1]}${match[2]}`]
      const type = this._getQuestionTypeByRef(path[0])

      if (match[3]) (path[path.length - 1] += `E${match[3]}`) && setIn(['path', 'e'], parseInt(match[3], 10), obj)
      if (match[4]) (path[path.length - 1] += `SQ${match[4]}`) && setIn(['path', 'sq'], parseInt(match[4], 10), obj)
      if (match[5]) {
        if (['ro', 'aro'].includes(type))
          (path[path.length - 1] += `SQ${match[5]}`) && setIn(['path', 'sq'], parseInt(match[5], 10), obj)
        else path.push(`A${match[5]}`) && setIn(['path', 'a'], parseInt(match[5], 10), obj)
      }
      if (match[6] && match[7]) {
        if ('cx' == type) {
          path.push(`${match[6]}${match[7]}`)
        } else {
          path.splice(0, path.length, path.join('') + `${match[6]}${match[7]}`)
        }
      } else if (match[5] && 'P' == match[8] && 'V' == match[9] && path.length && null != value) {
        if (['ro', 'aro'].includes(type)) path.push(`A${value}`)
        else {
          path[0] += `SQ${match[5]}`
          path.splice(-1, 1, `A${value}`)
          setIn(['path', 'sq'], parseInt(match[5], 10), obj)
        }
        setIn(['path', 'a'], value, obj)
        delete obj.v
      } else if (!match[3] && match[4] && match[5] && 'R' == match[6] && 'V' == match[9]) {
        path.splice(0, 2, path.join(''))
        !is(Array)(obj.v) && null != value && (obj.v = [obj.v])
      } else if ('C' == match[8]) {
        obj.t = obj.v
        path[path.length - 1] += match[8]
        delete obj.v
      } else if (
        ['hm', 'mn'].includes(type) &&
        match.reduce((r, m, i) => r && ([0, 1, 2, 4].includes(i) || !m), true)
      ) {
        path.splice(0, path.length, `${match[1]}${match[2]}`, `SQ${match[4]}`)
      }
      return { path, obj }
    } else if (['STATE', 'REGION'].includes(NAME)) {
      null != value && (obj.v = value.toUpperCase())
    }
    return { path: [NAME], obj }
  }

  _identifierToFilter(name, x = true, value = null) {
    const { path, obj } = this._parseIdentifier(name, x, value)
    if (path && path.length) {
      return setIn(path, obj, {})
    }
    return {}
  }

  _treeToFilter(node, filter) {
    const filters = filter || {}
    if ('LogicalExpression' === node.type && '&&' === node.operator) {
      this._treeToFilter(node.left, filters)
      this._treeToFilter(node.right, filters)
    } else if ('BinaryExpression' === node.type) {
      this._parseBinaryExpression(node, filters)
    } else if ('Identifier' === node.type) {
      assignDeep(filters, this._identifierToFilter(node.name))
    } else if ('UnaryExpression' === node.type && '!' === node.operator && 'Identifier' == node.argument.type) {
      assignDeep(filters, this._identifierToFilter(node.argument.name, false))
    } else if ('UnaryExpression' === node.type && '!' === node.operator && 'BinaryExpression' === node.argument.type) {
      this._parseBinaryExpression(node.argument, filters, true)
    } else if ('Literal' === node.type) {
      assignDeep(filters, this._identifierToFilter(`${node.value}`, true, node.value))
    } else {
      throw new ConvertToBasicError(SchemaRules.stringify(node))
    }
    return filters
  }

  _parseOECodingExpression(filter, variable, elements, operator, scope) {
    const name = `${variable.object.name}.${variable.property.name}${scope != undefined ? '.' + scope.name : ''}`
    const groupsMerge = ['codebook_in', 'tag_in'].includes(operator) && !Object.hasOwn(filter, name)
    elements.forEach(val => {
      Object.assign(
        filter,
        this._mergeBasicFilter(
          filter,
          this._identifierToFilter(name, true, is(String)(val.value) ? val.value.toLowerCase() : val.value),
          groupsMerge
        )
      )
    })
  }
  _parseOECodingsExpression(filter, variable, value, node) {
    const elements = value.elements || [value]
    if (Array.isArray(node.scope)) {
      node.scope.forEach(scope => {
        this._parseOECodingExpression(filter, variable, elements, node.operator, scope)
      })
    } else {
      this._parseOECodingExpression(filter, variable, elements, node.operator)
    }
  }

  _parseBinaryExpression(node, filter, inverse = false) {
    const [variable, value] = ['Identifier', 'MemberExpression'].includes(node.left.type)
      ? [node.left, node.right]
      : [node.right, node.left]
    if (
      ('Literal' === variable.type && 'Literal' === value.type) ||
      ('Identifier' === variable.type && 'Identifier' === value.type && 'in_section' !== node.operator) ||
      'BinaryExpression' === variable.type ||
      'BinaryExpression' === value.type ||
      'MemberExpression' === variable.type ||
      'MemberExpression' === value.type
    ) {
      if (['codebook_eq', 'codebook_neq', 'tag_eq', 'tag_neq'].includes(node.operator)) {
        if ('MemberExpression' === variable.type && 'Literal' === value.type) {
          this._parseOECodingsExpression(filter, variable, value, node)
        } else {
          throw new ConvertToBasicError(SchemaRules.stringify(node))
        }
      } else if (['codebook_in', 'tag_in'].includes(node.operator)) {
        if ('MemberExpression' === variable.type && 'ArrayExpression' === value.type) {
          this._parseOECodingsExpression(filter, variable, value, node)
        } else {
          throw new ConvertToBasicError(SchemaRules.stringify(node))
        }
      } else {
        filter.errors = [`${variable.name} ${node.operator} ${value.value}`]
      }
    } else {
      const operators = this.operators[variable.name.toLowerCase()]
      if (operators != undefined) {
        if (operators.includes(node.opeator)) {
          if ('Identifier' === variable.type && 'Literal' === value.type) {
            Object.assign(
              filter,
              this._mergeBasicFilter(
                filter,
                this._identifierToFilter(
                  variable.name,
                  true,
                  is(String)(value.value) ? value.value.toLowerCase() : value.value
                ),
                false
              )
            )
          } else {
            throw new ConvertToBasicError(SchemaRules.stringify(node))
          }
        } else {
          filter.errors = [`${variable.name} ${node.operator} ${value.value}`]
        }
      } else if (['==', '!=', '<>', 'codebook_eq', 'codebook_neq', 'tag_eq', 'tag_neq'].includes(node.operator)) {
        if ('Identifier' === variable.type && 'Literal' === value.type) {
          if ([...this.arrTraits, ...this.arrVariables].includes(variable.name)) {
            const surveySchema = this.surveySchema
            const { variables = {}, pipingVariables = {}, customVariables = {} } = surveySchema

            const item =
              variables[variable.name.toLowerCase()] ||
              pipingVariables[variable.name.toLowerCase()] ||
              customVariables[variable.name.toLowerCase()]

            if (item != undefined && item.values.some(v => Array.isArray(v))) {
              Object.assign(
                filter,
                this._mergeBasicFilter(
                  filter,
                  this._identifierToFilter(
                    variable.name,
                    ['==', 'codebook_eq', 'tag_eq'].includes(node.operator),
                    String(value.value).toLowerCase()
                  ),
                  false
                )
              )
            } else {
              throw new ConvertToBasicError(SchemaRules.stringify(node))
            }
          } else if ('TAG' == variable.name && node.scope != undefined)
            node.scope.forEach(scope =>
              Object.assign(
                filter,
                this._mergeBasicFilter(
                  filter,
                  this._identifierToFilter(
                    `${variable.name}.${scope.name}`,
                    ['==', 'codebook_eq', 'tag_eq'].includes(node.operator),
                    is(String)(value.value) ? value.value.toLowerCase() : value.value
                  ),
                  false
                )
              )
            )
          else
            Object.assign(
              filter,
              this._mergeBasicFilter(
                filter,
                this._identifierToFilter(
                  variable.name,
                  ['==', 'codebook_eq', 'tag_eq'].includes(node.operator),
                  is(String)(value.value) ? value.value.toLowerCase() : value.value
                ),
                false
              )
            )
        } else {
          throw new ConvertToBasicError(SchemaRules.stringify(node))
        }
      } else if (['>=', '<='].includes(node.operator)) {
        if (
          'Identifier' === variable.type &&
          'Literal' === value.type &&
          ![...this.arrTraits, ...this.arrVariables].includes(variable.name)
        ) {
          const index = ('>=' === node.operator) ^ (variable === node.left)
          assignDeep(filter, this._identifierToFilter(variable.name, true))
          const { path } = this._parseIdentifier(variable.name, true)

          getIn([...path, 'raw'], filter) || setIn([...path, 'raw'], [], filter)
          setIn(
            [...path, 'raw', index],
            (0 == index ? Math.max : Math.min)(
              ...[getIn([...path, 'raw', index], filter), parseFloat(value.value, 10)].filter(v => null != v)
            ),
            filter
          )
          2 == getIn([...path, 'raw'], filter).filter(x => null != x).length &&
            setIn([...path, 'v'], [getIn([...path, 'raw'], filter).join('-')], filter)
        } else {
          throw new ConvertToBasicError(SchemaRules.stringify(node))
        }
      } else if (['in', 'not_in', 'codebook_in', 'tag_in'].includes(node.operator)) {
        const groupsMerge = !Object.prototype.hasOwnProperty.call(filter, variable.name)
        const elements = value.elements || [value]

        if ([...this.arrTraits, ...this.arrVariables].includes(variable.name)) {
          const surveySchema = this.surveySchema
          const { variables = {}, pipingVariables = {}, customVariables = {} } = surveySchema

          const item =
            variables[variable.name.toLowerCase()] ||
            pipingVariables[variable.name.toLowerCase()] ||
            customVariables[variable.name.toLowerCase()]

          if (
            'Identifier' === variable.type &&
            'Literal' === value.type &&
            (item == undefined || !item.values.some(v => Array.isArray(v)))
          ) {
            Object.assign(
              filter,
              this._mergeBasicFilter(
                filter,
                this._identifierToFilter(
                  variable.name,
                  ['in', 'codebook_in', 'tag_in'].includes(node.operator),
                  String(elements.map(v => v.value)).toLowerCase()
                ),
                groupsMerge
              )
            )
          } else {
            throw new ConvertToBasicError(SchemaRules.stringify(node))
          }
        } else if ('TAG' == variable.name && node.scope != undefined)
          elements.forEach(element =>
            node.scope.forEach(scope =>
              Object.assign(
                filter,
                this._mergeBasicFilter(
                  filter,
                  this._identifierToFilter(
                    `${variable.name}.${scope.name}`,
                    ['in', 'codebook_in', 'tag_in'].includes(node.operator),
                    is(String)(element.value) ? element.value.toLowerCase() : element.value
                  ),
                  groupsMerge
                )
              )
            )
          )
        else
          elements.forEach(element =>
            Object.assign(
              filter,
              this._mergeBasicFilter(
                filter,
                this._identifierToFilter(
                  variable.name,
                  ['in', 'codebook_in', 'tag_in'].includes(node.operator),
                  is(String)(element.value) ? element.value.toLowerCase() : element.value
                ),
                groupsMerge
              )
            )
          )
      } else if (['in_shape'].includes(node.operator)) {
        const { path, obj } = this._parseIdentifier(variable.name, !inverse)
        const match = path[0].match(/^q\d+/i)
        const qUid = match[0].toLowerCase()
        const questionClusters = this.clusters.filter(c => c.qid == qUid) || []
        const clusterTitles =
          'ArrayExpression' == value.type ? value.elements.map(element => element.value) : [value.value]
        clusterTitles.forEach(title => {
          const cluster = questionClusters.find(cl => 0 === collator.compare(title, cl.title))
          if (cluster) Object.assign(filter, this._mergeBasicFilter(filter, setIn(path.concat([cluster.uid]), obj, {})))
          else filter.errors = [`${variable.name} ${node.operator} ${title}`]
        })
      } else if ('in_section' === node.operator) {
        const { path, obj } = this._parseIdentifier(variable.name, !inverse)
        const qRef = path[0].match(/^q\d+/i)?.[0]
        const question = qRef ? this._getQuestionByRef(qRef) : null
        const sections = question?.sections || []
        const avKey = path.length > 1 ? path[1] : null

        if ('Identifier' === value.type) {
          const sqMatch = value.name.match(/sq(\d+)$/i)
          if (!sqMatch || !avKey) throw new ConvertToBasicError(SchemaRules.stringify(node))
          const section = sections.find(s => s.position == parseInt(sqMatch[1], 10))
          if (section) {
            Object.assign(
              filter,
              this._mergeBasicFilter(filter, setIn([`${qRef}SQ${section.position}`, avKey], obj, {}))
            )
          } else {
            filter.errors = [`${variable.name} ${node.operator} ${value.name}`]
          }
        } else {
          const sectionTitles =
            'ArrayExpression' == value.type ? value.elements.map(element => element.value) : [value.value]
          sectionTitles.forEach(title => {
            const section = sections.find(s => 0 === collator.compare(title, s.text))
            if (section) {
              if (avKey) {
                Object.assign(
                  filter,
                  this._mergeBasicFilter(filter, setIn([`${qRef}SQ${section.position}`, avKey], obj, {}))
                )
              } else {
                filter.errors = [`${variable.name} ${node.operator} ${title}`]
              }
            } else {
              filter.errors = [`${variable.name} ${node.operator} ${title}`]
            }
          })
        }
      } else {
        throw new ConvertToBasicError(SchemaRules.stringify(node))
      }
    }
  }

  _minAgeRangeLowerBound() {
    const surveySchema = this.surveySchema
    const trait = surveySchema.traits.find(t => 'age' == t.id)
    const [minAgeRange] = trait.values.map(v => v.id).slice(0, 1)
    return [parseInt(minAgeRange.split('-', 2)[0], 10), minAgeRange]
  }

  _maxAgeRangeLowerBound() {
    const surveySchema = this.surveySchema
    const trait = surveySchema.traits.find(t => 'age' == t.id)
    const [maxAgeRange] = trait.values.map(v => v.id).slice(-1)
    return [
      parseInt(maxAgeRange.includes('+') ? maxAgeRange.replace('+', '') : maxAgeRange.split('-', 2)[0], 10),
      maxAgeRange
    ]
  }
  _maxAgeRangeUpperBound() {
    const surveySchema = this.surveySchema
    const trait = surveySchema.traits.find(t => 'age' == t.id)
    const [maxAgeRange] = trait.values.map(v => v.id).slice(-1)
    return [parseInt(maxAgeRange.includes('+') ? 1000000 : maxAgeRange.split('-', 2)[1], 10), maxAgeRange]
  }

  _filterEq = (f1, f2) => {
    if (f1 == f2) return true
    if (is(Object)(f1) && is(Object)(f2)) {
      return Object.keys(f1)
        .filter(k => k !== 'raw')
        .reduce((res, k) => res && this._filterEq(f1[k], f2[k]), true)
    } else if (is(Array)(f1) || is(Array)(f2)) {
      const af1 = is(Array)(f1) ? f1 : [f1]
      const af2 = is(Array)(f2) ? f2 : [f2]
      const sa = af1.length < af2.length ? af1 : af2
      const la = af1.length > af2.length ? af1 : af2
      return sa.reduce((res, v) => res && la.includes(v), true)
    } else {
      return is(String)(f1) && is(String)(f1) ? f1.toLowerCase() == f2.toLowerCase() : f1 == f2
    }
  }

  _mergeBasicFilter(filters, filter, groupsMerge = true, checkDiffs = false) {
    if (checkDiffs) {
      const diffKeys = Object.keys(filters).reduce((diff, key) => {
        const res = this._filterEq(filter[key], filters[key])
        return diff + (res ? 0 : 1)
      }, 0)
      if (1 < diffKeys) {
        if (filters.errors && is(Object)(filters.errors)) {
          filters.errors = Object.keys(filters.errors).length ? [filters.errors] : []
        } else if (!filters.errors) {
          filters.errors = []
        }
        filters.errors.push(filter)
        return filters
      }
    }

    const surveySchema = this.surveySchema

    return Object.keys(filter).reduce((result, key) => {
      let filterObject = filter[key]

      if (groupsMerge && 'AGE' === key && surveySchema.traits.some(t => 'age' == t.id)) {
        const [maxAge, maxAgeRange] = this._maxAgeRangeLowerBound()
        if (null == filter[key].v && filter[key].raw[0] == maxAge) {
          filterObject = assocPath(['v'], [maxAgeRange])(filter[key])
        }
      }

      const type = this._getQuestionTypeByRef(key)
      if ('hm' === type && checkDiffs && Object.hasOwn(filters, key)) {
        const clusters = this.clusters.filter(c => key.toLowerCase() == c.qid).map(c => c.uid)

        const negatives = Object.keys(filterObject).filter(k => !filterObject[k].x)
        for (const negative of negatives) {
          const keys = clusters.includes(negative)
            ? Object.keys(filters[key]).filter(k => clusters.includes(k))
            : Object.keys(filters[key]).filter(k => !clusters.includes(k))

          if (0 < keys.length && keys.every(k => k !== negative || (k === negative && filters[key][k].x))) {
            result.errors || (result.errors = [])
            result.errors.push({ [key]: filterObject })
            return result
          }
        }

        if (
          !negatives.length &&
          0 < Object.keys(filters[key]).length &&
          Object.keys(filters[key]).some(k => !filters[key][k].x)
        ) {
          result.errors || (result.errors = [])
          result.errors.push({ [key]: filterObject })
          return result
        }
      }

      const isBasic =
        (type && this._isBasicQuestionFilter(key, filterObject)) ||
        this._isBasicVariableFilter(key, filterObject) ||
        this._isBasicTagFilter(key, filterObject)

      if (groupsMerge && type && !isBasic) {
        result.errors || (result.errors = [])
        result.errors.push({ [key]: filterObject })
        return result
      }

      if (!Object.hasOwn(result, key)) {
        return assocPath([key], filterObject)(result)
      }

      const v = getIn([key, 'v'], result)
      const x = getIn([key, 'x'], result)

      if (v != undefined && null !== v && x) {
        return (is(Array)(v) ? v.includes(filterObject.v) : casecmp(v, filterObject.v))
          ? result
          : assocPath(
              [key, 'v'],
              groupsMerge
                ? uniq((is(Array)(filters[key].v) ? filters[key].v : [filters[key].v]).concat(filterObject.v))
                : uniq((is(Array)(filters[key].v) ? filters[key].v : [filters[key].v]).map(v => v + filterObject.v))
            )(result)
      }

      return assocPath([key], assignDeep({}, result[key], filterObject))(result)
    }, filters)
  }

  _cleanUpMergedFilter(filters) {
    Object.keys(filters).forEach(key => {
      if (Object.hasOwn(filters, key) && !is(Array)(filters[key]) && is(Object)(filters[key])) {
        filters[key] = Object.keys(filters[key])
          .filter(k => !['raw', 'path'].includes(k))
          .reduce(
            (result, k) => (
              (result[k] =
                is(Array)(filters[key][k]) || !is(Object)(filters[key][k])
                  ? filters[key][k]
                  : omit(['raw', 'path'], filters[key][k])),
              result
            ),
            {}
          )
        if (!Object.hasOwn(filters[key], 'v') && !Object.hasOwn(filters[key], 'x')) {
          filters[key] = Object.keys(filters[key])
            .sort((a, b) => parseInt(a.replace(/^A|SQ/, ''), 10) - parseInt(b.replace(/^A|SQ/, ''), 10))
            .reduce((result, k) => ((result[k] = filters[key][k]), result), {})
        }
      }
    })
  }

  _quotedStringToAytmLogic(str) {
    // convert string with quotes & double-quotes to a corresponding aytm logic "STR STR+" expression
    const q = str.includes("'")
    const dq = str.includes('"')
    if (!q) {
      return `'${str}'`
    } else if (q && !dq) {
      return `"${str}"`
    } else {
      const cq = str.match(/'/g).length
      const cdq = str.match(/"/g).length
      const split = cq < cdq ? str.split("'").map(v => `'${v}'`) : str.split('"').map(v => `"${v}"`)
      return split
        .slice(0, -1)
        .reduce((a, v) => a.concat([v, cq < cdq ? `"'"` : `'"'`]), [])
        .concat(split.slice(-1))
        .join('')
    }
  }

  _convertToDefaultAdvanced(key, value, opt, dim) {
    const refs = key.split('.')
    const ref = refs[0].toUpperCase()
    const prefix =
      'TAG' == ref
        ? 2 < refs.length
          ? `tag.sentiment ${refs[2].toUpperCase()}`
          : `tag ${refs[1].toUpperCase()}`
        : key.toLowerCase()

    const surveySchema = this.surveySchema
    const { variables = {}, pipingVariables = {}, customVariables = {} } = surveySchema || {}

    const variable =
      variables[key.toLowerCase()] || pipingVariables[key.toLowerCase()] || customVariables[key.toLowerCase()]

    let h = ''

    if (Object.hasOwn(value, 'v')) {
      const hp = { p: [], n: [] }

      const values = Array.isArray(value.v) ? [...value.v] : [value.v]
      for (const item of values) {
        if (variable != undefined && 'a' == surveySchema.refsList[key.toUpperCase()]) {
          const v = variable.values.find(_v => String(_v) === item)
          if (Array.isArray(v)) hp[(false === value.x && 'n') || 'p'].push(v)
          else hp[(false === value.x && 'n') || 'p'].push(this._quotedStringToAytmLogic(item))
        } else
          hp[(false === value.x && 'n') || 'p'].push(
            'string' === typeof item
              ? ('TAG' == ref ||
                  't' == surveySchema.refsList[key.toUpperCase()] ||
                  'a' == surveySchema.refsList[key.toUpperCase()]) &&
                'true' != item.toLowerCase()
                ? this._quotedStringToAytmLogic(item)
                : item
              : item
          )
      }

      if ('a' == surveySchema.refsList[key.toUpperCase()]) {
        if (0 < hp.p.length) {
          h = `${1 < dim && 1 < hp.p.length ? '(' : ''}${hp.p
            .map(v => (Array.isArray(v) ? `${key.toLowerCase()} = '${v.join(',')}'` : `${v} in ${key.toLowerCase()}`))
            .join(' or ')}${1 < dim && 1 < hp.p.length ? ')' : ''}`
        }
        if (0 < hp.n.length) {
          h = `${1 < dim && 1 < hp.n.length ? '(' : ''}${hp.n
            .map(v =>
              Array.isArray(v) ? `${key.toLowerCase()} != '${v.join(',')}'` : `${v} not in ${key.toLowerCase()}`
            )
            .join(' or ')}${1 < dim && 1 < hp.n.length ? ')' : ''}`
        }
      } else {
        if (0 < hp.p.length) {
          h = `${prefix} ${1 < hp.p.length ? 'in' : '='} ${hp.p.join(', ')}`
        }
        if (0 < hp.n.length) {
          h = `${h}${0 < h.length ? ' and ' : ''}${prefix} ${1 < hp.n.length ? 'not in' : '!='} ${hp.n.join(', ')}`
        }
      }

      return h
    } else if (variable != undefined && 'x' == surveySchema.refsList[key.toUpperCase()]) {
      const a = []

      const values = Object.keys(value)
      for (const item of values) {
        const k = `${key.toLowerCase()}_${item}`

        a.push(this._convertToDefaultAdvanced(k, { v: [value[item].x ? 1 : 0], x: true }, opt, dim))
      }

      if (0 < a.length) {
        h = `${1 < dim && 1 < a.length ? '(' : ''}${a.join(' and ')}${1 < dim && 1 < a.length ? ')' : ''}`
      }

      return h
    }

    return opt.unary ? prefix : ''
  }
  _convertToQuestionAdvanced(key, value, question, opt, dim) {
    if (0 < Object.keys(value).length) {
      const type = question.variant || question.type

      if (value.v != undefined) {
        const range = value.v
          .toString()
          .replace(/^(.+?)-(.+)$/, '$1##$2')
          .split(/##/)
        const p = 'psc' == type ? 'V' : 'RV'
        return `${key}${p} >= ${range[0]} and ${key}${p} <= ${range[1]}`
      }

      if (value.x != undefined) {
        return `${false === value.x ? 'not ' : ''}${key}`
      }

      if ('mn' === type && /sq\d+$/i.test(key)) {
        const qRef = key.match(/^(q\d+)/i)[1]
        const sqMatch = key.match(/sq(\d+)$/i)
        const sqPosition = parseInt(sqMatch[1], 10)
        const section = (question.sections || []).find(s => s.position == sqPosition)
        if (!section) return ''
        const h = { p: [], n: [] }

        for (const [_key, _value] of Object.entries(value)) {
          h[false === _value.x ? 'n' : 'p'].push(
            `${false === _value.x ? 'not ' : ''}${qRef}${_key} IN SECTION ${this._quotedStringToAytmLogic(section.text)}`
          )
        }

        const result = []
        if (0 < h.p.length) result.push(1 < h.p.length ? `(${h.p.join(' or ')})` : h.p[0])
        if (0 < h.n.length) result.push(h.n.join(' and '))
        return result.join(' and ')
      }

      const clusters = this.clusters.filter(c => `q${question.visual_position}` == c.qid)

      const h = { p: [], n: [] }
      const sw = ['cx', 'ps', 'psc', 'vk'].includes(type)
      const result = []

      for (const [_key, _value] of Object.entries(value)) {
        const cluster = clusters.find(c => _key == c.uid)
        if (cluster) {
          h[false === _value.x ? 'n' : 'p'].push(
            `${false === _value.x ? 'not ' : ''}${key}R in cluster "${cluster.title}"`
          )
        } else {
          if ('cx' == type) {
            const values = Array.isArray(_value.v) ? [..._value.v] : [_value.v]
            for (const j in values) {
              h[false === _value.x ? 'n' : 'p'].push(
                `${key}${_key}P ${false === _value.x ? '!=' : '='} ${this._quotedStringToAytmLogic(values[j])}`
              )
            }
          } else if (['ro', 'aro'].includes(type)) {
            h[false === _value.x ? 'n' : 'p'].push(
              `${key.replace('SQ', 'A')}PV ${false === _value.x ? '!=' : '='} ${_key.slice(1)}`
            )
          } else {
            h[false === _value.x ? 'n' : 'p'].push(`${false === _value.x ? 'not ' : ''}${key}${_key}`)
          }
        }
      }

      if (0 < h.p.length) {
        result.push(
          `${!sw && ((1 < dim && 1 < h.p.length) || 0 < h.n.length) ? '(' : ''}${h.p.join(sw ? ' and ' : ' or ')}${
            !sw && ((1 < dim && 1 < h.p.length) || 0 < h.n.length) ? ')' : ''
          }`
        )
      }
      if (0 < h.n.length) {
        result.push(h.n.join(' and '))
      }

      return result.join(' and ')
    }

    return opt.unary ? key.toUpperCase() : ''
  }
  convertToAdvanced(tree, opt = {}) {
    const entries = Object.entries(tree)
    return entries
      .map(([key, value]) => {
        const surveySchema = this.surveySchema
        const { variables = {}, pipingVariables = {}, customVariables = {} } = surveySchema

        const variable = key.toLowerCase()
        const question = this._getQuestionByRef(key)

        return Object.hasOwn(variables, variable) ||
          Object.hasOwn(pipingVariables, variable) ||
          Object.hasOwn(customVariables, variable) ||
          null === question
          ? this._convertToDefaultAdvanced(key, value, opt, entries.length)
          : question != undefined
            ? this._convertToQuestionAdvanced(key, value, question, opt, entries.length)
            : ''
      })
      .filter(v => v.length)
      .join(' and ')
  }
}

function convertToGroups(node, groups = []) {
  if ('LogicalExpression' === node.type && '||' === node.operator) {
    const { left, right } = node
    if ('||' === left.operator && '||' !== right.operator) {
      groups.push(right)
      convertToGroups(left, groups)
    }
    if ('||' === right.operator && '||' !== left.operator) {
      groups.push(left)
      convertToGroups(right, groups)
    }
    if ('||' === left.operator && '||' === right.operator) {
      convertToGroups(left, groups)
      convertToGroups(right, groups)
    }
    if ('||' !== right.operator && '||' !== left.operator) {
      groups.push(left)
      groups.push(right)
    }
  } else {
    groups.push(node)
  }
  return groups
}
BasicFilters.convertToGroups = convertToGroups
